import React, { useEffect, useState } from "react";
import { Ban, Check, CircleCheck, CircleX, TriangleAlert } from "lucide-react";
import type { Message } from "../context";
import type {
    ActionResultData, ActionResultStatus, AgentCardData, AnswerReply, QuestionData, QuestionOption,
} from "../types";

interface AgentPartProps {
    message: Message;
    // True for an open id, on its latest card or question row.
    isOpen: boolean;
    // isOpen, and no turn streams. Only a live control takes a click.
    isLive: boolean;
    // The option ids a click sent for this row. Empty when the coach typed or did not answer.
    picked: string[];
    onAnswer: (reply: AnswerReply) => void;
}

type QuestionProps = Omit<AgentPartProps, "message"> & { question: QuestionData };

// A live chip shows no pick: a failed send leaves the question open, and the coach clicks again.
const ChoiceChips: React.FC<QuestionProps> = ({ question, isLive, picked, onAnswer }) => (
    <div className="chat-v2-agent-chips" role="group" aria-label={question.text}>
        {question.options.map(option => {
            const isPicked = !isLive && picked.includes(option.id);
            return (
                <button
                    key={option.id}
                    type="button"
                    className={`chat-v2-agent-chip${isPicked ? " is-picked" : ""}`}
                    disabled={!isLive}
                    aria-pressed={isPicked}
                    onClick={() => { if (isLive) onAnswer({ questionId: question.question_id, choice: option }); }}
                >
                    {isPicked && <Check size={14} aria-hidden="true" />}
                    {option.label}
                </button>
            );
        })}
    </div>
);

const MultiChoice: React.FC<QuestionProps> = ({ question, isOpen, isLive, picked, onAnswer }) => {
    const [checked, setChecked] = useState<Set<string>>(() => new Set());
    // The checks survive a failed send (isTyping only disables the boxes). They clear once the
    // question leaves the open list: the user bubble is then the record of the choice.
    useEffect(() => { if (!isOpen) setChecked(prev => (prev.size === 0 ? prev : new Set())); }, [isOpen]);

    const toggle = (id: string) => setChecked(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const send = () => {
        if (!isLive || checked.size === 0) return;
        const choices: QuestionOption[] = question.options.filter(o => checked.has(o.id));
        onAnswer({ questionId: question.question_id, choices });
    };
    // A closed question shows the boxes the coach sent.
    const shown = isOpen ? checked : new Set(picked);

    return (
        <div className="chat-v2-agent-multi" role="group" aria-label={question.text}>
            {question.options.map(option => (
                <label key={option.id} className={`chat-v2-agent-check${shown.has(option.id) ? " is-checked" : ""}`}>
                    <input
                        type="checkbox"
                        checked={shown.has(option.id)}
                        disabled={!isLive}
                        onChange={() => toggle(option.id)}
                    />
                    {option.label}
                </label>
            ))}
            <button
                type="button"
                className="chat-v2-agent-chip chat-v2-agent-send"
                disabled={!isLive || checked.size === 0}
                aria-label={`Send answer: ${question.text}`}
                onClick={send}
            >
                Send{checked.size > 0 && <span className="chat-v2-agent-send-count">{checked.size}</span>}
            </button>
        </div>
    );
};

const QUESTION_VIEWS: Record<QuestionData["answer_type"], React.FC<QuestionProps>> = {
    choice: ChoiceChips,
    multi_choice: MultiChoice,
    text: ({ isOpen }) => (isOpen ? <div className="chat-v2-agent-hint">Type your answer below.</div> : null),
};

const QuestionPart: React.FC<QuestionProps> = (props) => {
    const { question } = props;
    const View = QUESTION_VIEWS[question.answer_type];
    return (
        <div className="chat-v2-agent-question">
            <div className="chat-v2-agent-question-text">{question.text}</div>
            <View {...props} />
        </div>
    );
};

// "Not saved yet" keys on isOpen, not isLive: a card stays open while its own answer streams.
// A closed card has no tag. The picked chip and the result row tell what happened.
const ActionCard: React.FC<AgentPartProps> = ({ message, ...rest }) => {
    const data = message.metadata.data as AgentCardData;
    return (
        <div className={`chat-v2-agent-card${rest.isOpen ? " is-open" : " is-closed"}`}>
            <div className="chat-v2-agent-card-header">
                <span className="chat-v2-agent-title">{message.metadata.title}</span>
                {rest.isOpen && <span className="chat-v2-agent-status">Not saved yet</span>}
            </div>
            {data.rows.length > 0 && (
                <ul className="chat-v2-agent-rows">
                    {data.rows.map((row, i) => <li key={i}>{row}</li>)}
                </ul>
            )}
            <QuestionPart question={data.question} {...rest} />
        </div>
    );
};

// A bare question draws its text in the header, so it needs no QuestionPart.
const Question: React.FC<AgentPartProps> = ({ message, ...rest }) => {
    const question = message.metadata.data as QuestionData;
    const View = QUESTION_VIEWS[question.answer_type];
    return (
        <div className={`chat-v2-agent-card${rest.isOpen ? " is-open" : " is-closed"}`}>
            <div className="chat-v2-agent-card-header">
                <span className="chat-v2-agent-question-text">{question.text}</span>
            </div>
            <View question={question} {...rest} />
        </div>
    );
};

const RESULT_VIEWS: Record<ActionResultStatus, { Icon: React.FC<{ size?: string | number }>; cls: string }> = {
    saved: { Icon: CircleCheck, cls: "chat-v2-agent-result-saved" },
    refused: { Icon: CircleX, cls: "chat-v2-agent-result-refused" },
    failed: { Icon: TriangleAlert, cls: "chat-v2-agent-result-failed" },
    cancelled: { Icon: Ban, cls: "chat-v2-agent-result-cancelled" },
};

const ActionResult: React.FC<AgentPartProps> = ({ message }) => {
    const data = message.metadata.data as ActionResultData;
    const { Icon, cls } = RESULT_VIEWS[data.status];
    const title = message.metadata.title;
    return (
        <div className={`chat-v2-agent-result ${cls}${message.fresh ? " chat-v2-artifact-animate" : ""}`} role="status">
            <Icon size="1.1em" aria-hidden="true" />
            <span>
                {title && <span className="chat-v2-agent-title">{title} </span>}
                {data.message}
            </span>
        </div>
    );
};

const VIEWS: Record<"action_card" | "question" | "action_result", React.FC<AgentPartProps>> = {
    action_card: ActionCard,
    question: Question,
    action_result: ActionResult,
};

const AgentPart: React.FC<AgentPartProps> = (props) => {
    const View = VIEWS[props.message.type as keyof typeof VIEWS];
    return <View {...props} />;
};

export default AgentPart;
